import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'
import { useCan } from '../../contexts/PermissionsContext'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import {
  useContractTypeProperties,
  useExtractionReviewStats,
  type ExtractionReviewStat,
  type PropertyDefinition,
} from '../../hooks/useQueries'
import { ORG_REVIEWS_MIN } from './ClauseReliability'

const TIER_VARIANT = { high: 'success', medium: 'warning', low: 'destructive', unmeasured: 'secondary' } as const

function pct(n?: number | null): string {
  return n == null ? '—' : `${Math.round(n * 100)} %`
}

// « À quel niveau de contrôle » (spec 2026-10-27-12 §E): per extractable
// clause of this exact type, the control level, the reference precision and
// fill rate with their counts, the model and the date of the measure, and the
// organization's own reviews.
export function ContractTypeExtractionReliability({ org, contractTypeUid, contractTypeCode }: {
  org: string
  contractTypeUid: string
  contractTypeCode: string
}) {
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()
  const { formatDate } = useFormatters()
  const canReadContracts = useCan('contracts.read')
  const { data: bindings = [] } = useContractTypeProperties(org, contractTypeUid)
  const { data: reviews } = useExtractionReviewStats(org, contractTypeCode, { enabled: canReadContracts })
  const reviewsByCode = useMemo(() => {
    const map = new Map<string, ExtractionReviewStat>()
    for (const r of reviews) map.set(r.property_code, r)
    return map
  }, [reviews])
  const rows = useMemo(
    () =>
      bindings
        .filter(b => b.extraction_reliability && b.property_definition)
        .map(b => ({ def: b.property_definition as PropertyDefinition, rel: b.extraction_reliability! }))
        .sort((a, b) => a.def.code.localeCompare(b.def.code)),
    [bindings],
  )
  const name = (def: PropertyDefinition) => {
    const names = def.name as Record<string, string> | undefined
    return names?.[locale] || names?.en || names?.fr || def.code
  }

  return (
    <Card data-testid="contract-type-extraction-reliability">
      <CardHeader>
        <CardTitle className="text-base">{t('type_reliability.title')}</CardTitle>
        <CardDescription>{t('type_reliability.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState variant="inline" title={t('type_reliability.empty')} data-testid="contract-type-extraction-reliability-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">{t('type_reliability.clause')}</th>
                  <th className="py-1 pr-3 font-medium">{t('type_reliability.control')}</th>
                  <th className="py-1 pr-3 font-medium">{t('type_reliability.precision')}</th>
                  <th className="py-1 pr-3 font-medium">{t('type_reliability.fill_rate')}</th>
                  <th className="py-1 pr-3 font-medium">{t('type_reliability.model')}</th>
                  <th className="py-1 pr-3 font-medium">{t('type_reliability.measured_to')}</th>
                  <th className="py-1 font-medium">{t('type_reliability.org_reviews')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map(({ def, rel }) => {
                  const measured = rel.tier !== 'unmeasured'
                  const review = reviewsByCode.get(def.code)
                  return (
                    <tr key={def.code} data-testid={`contract-type-reliability-row-${def.code}`} data-tier={rel.tier}>
                      <td className="py-1.5 pr-3">{name(def)}</td>
                      <td className="py-1.5 pr-3">
                        <Badge variant={TIER_VARIANT[rel.tier]} className="text-[10px] font-normal">
                          {t(`clauses.reliability.control.${rel.control_level}` as any)}
                        </Badge>
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums">
                        {measured ? `${pct(rel.precision)} (${t('clauses.reliability.correct', { correct: Math.round(rel.correct), filled: rel.filled })})` : t('type_reliability.not_measured')}
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums">
                        {measured ? `${pct(rel.fill_rate)} (${rel.filled}/${rel.observed})` : '—'}
                      </td>
                      <td className="py-1.5 pr-3">{rel.model || '—'}</td>
                      <td className="py-1.5 pr-3">{rel.measured_to ? formatDate(rel.measured_to) : '—'}</td>
                      <td className="py-1.5 tabular-nums" data-testid={`contract-type-reliability-reviews-${def.code}`}>
                        {review && review.reviews > 0
                          ? `${t('type_reliability.org_reviews_value', { confirmed: review.confirmed, count: review.reviews })}${review.reviews >= ORG_REVIEWS_MIN ? ` (${pct(review.precision)})` : ''}`
                          : t('type_reliability.no_reviews')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
